import styled from '@emotion/styled';

import './assets/icons/custom-icons';
import { NavinaDesignSystem, NavinaThemeMode } from '@navina/design-system';
import { ConfigProvider } from 'antd';
import { lazy, StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { config as zodConfig } from 'zod/mini';

import { App } from './App';
import { isEpicLoginRedirectHop } from './authentication/isEpicLoginRedirectHop';
import { getOrCreateDatadogLogsModel } from './components/Datadog/DatadogLogModel';
import { InitializationError } from './components/ErrorPage/Errors';
import { getOrCreateInitializedLogRocketModel } from './components/LogRocket/LogRocketModel';
import {
	APPCUES_ACCOUNT_ID,
	APPCUES_ENABLED,
	DATADOG_APPLICATION_ID,
	DATADOG_CLIENT_TOKEN,
	DATADOG_ENVIRONMENT_NAME,
} from './config/Environment';
import { sendOnClickAnalytics } from './index.analytics';
import '@ant-design/v5-patch-for-react-19';

import { AuthStoreContext, getOrCreateAuthStore } from './stores/authStore';
import antdTheme from './theme/antd-tokens/light.json' with { type: 'json' };
import { ANTD_COMPONENTS_CONFIG } from './theme/theme';
import { withAnalyticNamespaceOnClickEventDelegation } from './utilities/analytics/eventDelegation';

zodConfig({
	jitless: true,
});

const AppcuesStarterLazy = lazy(() =>
	import('./third-parties/AppcuesStarter').then((module) => ({
		default: module.AppcuesStarter,
	})),
);

// Real User Monitoring lives in its own chunk that is fetched, parsed, and initialized lazily.
// It must never gate the first render (previously a top-level `await import(...)` suspended the
// entire entry module until this resolved, delaying React mount on every SSO redirect hop).
function initializeDatadogRealUserMonitoring(applicationId: string, clientToken: string): void {
	void import('@datadog/browser-rum').then(({ datadogRum }) => {
		datadogRum.init({
			applicationId,
			clientToken,
			site: 'datadoghq.com',
			service: 'navina-app',
			env: DATADOG_ENVIRONMENT_NAME,
			// Specify a version number to identify the deployed version of your application in Datadog
			// version: '1.0.0',
			sessionSampleRate: 100,
			sessionReplaySampleRate: 0,
			trackUserInteractions: true,
			trackResources: true,
			trackLongTasks: true,
			trackAnonymousUser: true,
			defaultPrivacyLevel: 'mask-user-input',
			enablePrivacyForActionName: true,
		});

		getOrCreateDatadogLogsModel('navina-frontend-app');
	});
}

function initializeMonitoring(): void {
	getOrCreateInitializedLogRocketModel();

	if (DATADOG_CLIENT_TOKEN && DATADOG_APPLICATION_ID) {
		initializeDatadogRealUserMonitoring(DATADOG_APPLICATION_ID, DATADOG_CLIENT_TOKEN);
	}
}

function scheduleMonitoringInitializationWhenIdle(): void {
	if (typeof globalThis.requestIdleCallback === 'function') {
		globalThis.requestIdleCallback(initializeMonitoring);
		return;
	}

	globalThis.setTimeout(initializeMonitoring, 0);
}

const NavinaAppContainer = styled.div`
	block-size: 100%;
	inline-size: 100%;
`;

const AuthStoreProvider = AuthStoreContext.Provider;
const authStore = getOrCreateAuthStore();

const container = document.getElementById('root');
if (container === null) {
	throw new InitializationError('Root container not found');
}

// On an Epic SMART launch redirect hop the standalone script (loaded first, in <head>) navigates
// away, so mounting React here would parse/execute the whole app for a page we immediately leave.
// This matters most for the async Epic 2nd-login redirect, where the deferred entry would otherwise
// boot the full app during the well-known config fetch that precedes the redirect.
if (!isEpicLoginRedirectHop()) {
	const root = createRoot(container);

	root.render(
		<StrictMode>
			<NavinaAppContainer
				{...{
					...withAnalyticNamespaceOnClickEventDelegation(sendOnClickAnalytics),
				}}
			>
				<AuthStoreProvider value={authStore}>
					<ConfigProvider theme={{ token: antdTheme, components: ANTD_COMPONENTS_CONFIG }}>
						<NavinaDesignSystem {...{ mode: NavinaThemeMode.Bright }}>
							<App />
						</NavinaDesignSystem>
					</ConfigProvider>
				</AuthStoreProvider>
			</NavinaAppContainer>
			{APPCUES_ENABLED && (
				<Suspense {...{ fallback: null }}>
					<AppcuesStarterLazy {...{ appcuesAccountId: APPCUES_ACCOUNT_ID }} />
				</Suspense>
			)}
		</StrictMode>,
	);

	scheduleMonitoringInitializationWhenIdle();
}
